import Box from '@mui/material/Box'
import { FC } from 'react'

import { DetailBooleanField, DetailCommentBlock, DetailField, DetailSection } from '../../common'
import { ConsumptionCertificateDetailsDTO } from 'models/purchaseFlow/consumptionCertificateDetailsDTO'
import { useTranslation } from 'react-i18next'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { GRAY_300, GRAY_700, GRAY_900 } from 'constants/styling/theme'

const T_PREFIX = 'gallery:assignment_details.energy_certificate'

// Purchase flow translation prefixes for enum values
const PF_CC_PROP = 'purchase_flow:consumption_certificate_step.property_information_page'
const PF_CC_INFO = 'purchase_flow:consumption_certificate_step.consumption_information_page'

interface Props {
  data: ConsumptionCertificateDetailsDTO
}

export const ConsumptionCertificateSection: FC<Props> = ({ data }) => {
  const { t } = useTranslation(['gallery', 'purchase_flow', 'heating_system'])
  const te = (prefix: string, value: string | null | undefined) => value ? t(`${prefix}.${value}`, { defaultValue: value }) : undefined

  return (
    <>
      {/* Property information */}
      <DetailSection title={t(`${T_PREFIX}.property.title`)}>

        <DetailField
          label={t(`${T_PREFIX}.property.building_type`)}
          value={te(`${PF_CC_PROP}.building_type`, data.buildingType)}
        />

        <DetailField
          label={t(`${T_PREFIX}.property.living_units`)}
          value={data.livingUnits}
        />

        <DetailField
          label={t(`${T_PREFIX}.consumption.living_units_description`)}
          value={te(`${T_PREFIX}.consumption.living_units_type`, data.livingUnitsDescr)}
        />

        <DetailField
          label={t(`${T_PREFIX}.property.building_location`)}
          value={te(`${PF_CC_PROP}.building_location_type`, data.buildingLocation)}
        />

        <DetailField
          label={t(`${T_PREFIX}.property.building_year`)}
          value={data.buildingYear}
        />

        <DetailField
          label={t(`${T_PREFIX}.consumption.renovation_year`)}
          value={data.renovationYear || undefined}
        />

        <DetailField
          label={t(`${T_PREFIX}.consumption.building_period`)}
          value={te(`${T_PREFIX}.consumption.building_period_type`, data.buildingPeriod)}
        />

        <DetailField
          label={t(`${T_PREFIX}.property.total_living_area`)}
          value={data.totalLivingArea ? `${data.totalLivingArea} m\u00B2` : undefined}
        />

        <DetailField
          label={t(`${T_PREFIX}.property.commercial_space`)}
          value={data.commercialSpaceInSquareMeters ? `${data.commercialSpaceInSquareMeters} m\u00B2` : undefined}
        />

        <DetailField
          label={t(`${T_PREFIX}.property.type_of_use_commercial`)}
          value={data.typeOfUseCommercial}
        />

      </DetailSection>

      {/* Energetic conditions */}
      <DetailSection title={t(`${T_PREFIX}.consumption.energetic_conditions.title`)}>

        <DetailBooleanField
          label={t(`${T_PREFIX}.consumption.energetic_conditions.double_glazed_windows`)}
          value={data.energeticRenovations.hasDoubleGlazedWindows}
        />

        <DetailBooleanField
          label={t(`${T_PREFIX}.consumption.energetic_conditions.roof_insulation`)}
          value={data.energeticRenovations.hasRoofInsulation}
        />

        <DetailBooleanField
          label={t(`${T_PREFIX}.consumption.energetic_conditions.exterior_wall_insulation`)}
          value={data.energeticRenovations.hasExteriorWallInsulation}
        />

        {data.currentEnergeticConditions?.length > 0 &&
          <DetailField label={t(`${T_PREFIX}.consumption.energetic_conditions.current_measures`)}>
            <Stack gap="0.2rem">
              {data.currentEnergeticConditions.map((measure) => (
                <Typography key={measure} variant="text-sm">
                  {te(`${PF_CC_INFO}.energetic_condition_type`, measure)}
                </Typography>
              ))}
            </Stack>
          </DetailField>
        }

      </DetailSection>

      {/* Energy sources */}
      <DetailSection title={t(`${T_PREFIX}.consumption.energy_sources.title`)}>

        <DetailField
          label={t(`${T_PREFIX}.consumption.energy_sources.renewable_energies`)}
          value={te(`${PF_CC_INFO}.system_type`, data.renewableEnergies)}
        />

        <DetailField
          label={t(`${T_PREFIX}.consumption.energy_sources.renewable_usage`)}
          value={te(`${PF_CC_INFO}.use_type`, data.renewableEnergiesUsage)}
        />

        <DetailField
          label={t(`${T_PREFIX}.consumption.energy_sources.ventilation`)}
          value={te(`${PF_CC_PROP}.ventilation_type`, data.ventilation)}
        />

        <DetailField
          label={t(`${T_PREFIX}.consumption.energy_sources.basement`)}
          value={te(`${PF_CC_PROP}.basement_type`, data.basement)}
        />

      </DetailSection>

      {/* Heating systems */}
      {data.heaters?.map((heater, index) => (
        <DetailSection
          key={index}
          title={t(`${T_PREFIX}.heating.title_n`, { n: index + 1 })}
        >

          <DetailField
            label={t(`${T_PREFIX}.consumption.heating.fuel`)}
            value={te('heating_system:fuel_type', heater.fuel)}
          />

          <DetailField
            label={t(`${T_PREFIX}.consumption.heating.unit`)}
            value={te('heating_system:unit_capture_type', heater.unit)}
          />

          <DetailField
            label={t(`${T_PREFIX}.consumption.heating.hot_water`)}
            value={te('heating_system:hot_water_type', heater.hotWater)}
          />

          <DetailField
            label={t(`${T_PREFIX}.heating.year_of_construction`)}
            value={heater.yearOfConstruction}
          />

          <DetailField
            label={t(`${T_PREFIX}.consumption.heating.primary_energy_factor`)}
            value={heater.primaryEnergyFactor || undefined}
          />

          {heater.billingPeriod?.length > 0 &&
            <DetailField
              label={t(`${T_PREFIX}.consumption.heating.billing_periods`)}
              contentSx={{
                'border': '0.1rem solid ' + GRAY_300,
                'border-radius': '0.4rem',
                'padding': '1.6rem',
              }}
            >
              <Stack gap="0.8rem">
                {heater.billingPeriod.map((period, periodIndex) => (
                  <Stack key={periodIndex} gap="0.2rem">

                    <BillingPeriodDetail
                      label={t(`${T_PREFIX}.consumption.heating.billing_period_n`, { n: periodIndex + 1 })}
                      value={`${period.startBillingPeriod} — ${period.endOfBillingPeriod}`}
                      direction="column"
                    />

                    <BillingPeriodDetail
                      label={t(`${T_PREFIX}.consumption.heating.consumption`)}
                      value={`${period.consumption} kWh`}
                    />

                    {!!period.hotWaterConsumptionKwh &&
                      <BillingPeriodDetail
                        label={t(`${T_PREFIX}.consumption.heating.hot_water_consumption`)}
                        value={`${period.hotWaterConsumptionKwh} kWh`}
                      />
                    }

                  </Stack>
                ))}
              </Stack>
            </DetailField>
          }

        </DetailSection>
      ))}

      {/* Vacancy */}
      {!!data.vacancy &&
        <DetailSection title={t(`${T_PREFIX}.consumption.vacancy.title`)}>

          <DetailBooleanField
            label={t(`${T_PREFIX}.consumption.vacancy.present`)}
            value={data.vacancy.vacancyPresent}
          />

          {data.vacancy.vacancyPresent && data.vacancy.vacancyPeriods?.length > 0 &&
            <DetailField label={t(`${T_PREFIX}.consumption.vacancy.periods`)}>
              <Stack gap="0.4rem">
                {data.vacancy.vacancyPeriods.map((period, index) => (
                  <DetailField
                    key={index}
                    value={`${period.startVacancy} — ${period.endVacancy} (${period.areaVacancyInSquareMeters} m\u00B2)`}
                  />
                ))}
              </Stack>
            </DetailField>
          }

        </DetailSection>
      }

      {/* Additional information */}
      <DetailSection title={t(`${T_PREFIX}.additional.title`)}>
        <DetailField
          label={t(`${T_PREFIX}.additional.reason`)}
          value={te('purchase_flow:additional_information_page.energy_certificate_reason', data.reasonForEnergyPass)}
        />
      </DetailSection>

      {!!data.messageToExpert &&
        <DetailSection title={t(`${T_PREFIX}.additional.message_to_expert`)}>
          <DetailCommentBlock>{data.messageToExpert}</DetailCommentBlock>
        </DetailSection>
      }
    </>
  )
}

const BillingPeriodDetail = ({ label, value, direction = 'row' }: { label: string, value: string, direction?: 'row' | 'column' }) => (
  <Stack fontSize="1.4rem" direction={direction} gap=".2rem">

    <Box fontWeight={500} color={GRAY_700}>
      {direction === 'row' ? `${label} -` : label}
    </Box>

    <Box fontWeight={350} color={GRAY_900}>
      {value}
    </Box>

  </Stack>
)
